import React, { useState, useEffect, useRef } from 'react';
import { 
  Upload, Download, Image as ImageIcon, 
  Facebook, Twitter, Linkedin, 
  Monitor, Smartphone, CheckCircle2,
  Palette, Shapes, Heart, Mail, Globe, Sparkles,
  Info, Briefcase, Code2, Store, LayoutDashboard
} from 'lucide-react';

const SOCIAL_SIZES = [
  { id: 'profile', name: 'صورة البروفايل (عام)', width: 1080, height: 1080, icon: ImageIcon, desc: 'واتساب، فيسبوك، انستجرام، لينكدإن' },
  { id: 'fb_cover', name: 'غلاف فيسبوك', width: 820, height: 312, icon: Facebook, desc: 'Facebook Page Cover' },
  { id: 'twitter_header', name: 'غلاف إكس (تويتر)', width: 1500, height: 500, icon: Twitter, desc: 'X / Twitter Header' },
  { id: 'linkedin_cover', name: 'غلاف لينكدإن للشركات', width: 1128, height: 191, icon: Linkedin, desc: 'LinkedIn Company Banner' },
  { id: 'story', name: 'حالة واتساب / ستوري', width: 1080, height: 1920, icon: Smartphone, desc: 'WhatsApp Status, IG Story' },
  { id: 'post_landscape', name: 'غلاف موقع / رابط (Open Graph)', width: 1200, height: 630, icon: Monitor, desc: 'Website Link Preview' },
];

const roundRectPath = (ctx, x, y, width, height, radius) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

const WhatsAppIcon = ({ className, color }) => (
  <svg className={className} fill={color || "currentColor"} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.888-.788-1.487-1.761-1.66-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
  </svg>
);

const SizeCard = ({ size, imageObj, bgColor, edgeStyle }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!imageObj || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = size.width;
    canvas.height = size.height;
    const bgGradient = ctx.createRadialGradient(canvas.width/2, canvas.height/2, 0, canvas.width/2, canvas.height/2, Math.max(canvas.width, canvas.height));
    bgGradient.addColorStop(0, bgColor);
    bgGradient.addColorStop(1, '#020617'); 
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const padding = Math.min(canvas.width, canvas.height) * 0.12;
    const availableWidth = canvas.width - padding * 2;
    const availableHeight = canvas.height - padding * 2;
    const scale = Math.min(availableWidth / imageObj.width, availableHeight / imageObj.height);
    const scaledWidth = imageObj.width * scale;
    const scaledHeight = imageObj.height * scale;
    const x = (canvas.width - scaledWidth) / 2;
    const y = (canvas.height - scaledHeight) / 2;
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    if (edgeStyle === 'vignette') {
      ctx.drawImage(imageObj, x, y, scaledWidth, scaledHeight);
      const innerRadius = Math.min(scaledWidth, scaledHeight) * 0.35;
      const outerRadius = Math.max(scaledWidth, scaledHeight) * 0.70; 
      const vignette = ctx.createRadialGradient(cx, cy, innerRadius, cx, cy, outerRadius);
      vignette.addColorStop(0, 'rgba(0,0,0,0)'); 
      vignette.addColorStop(0.8, bgColor); 
      vignette.addColorStop(1, bgColor); 
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    } else if (edgeStyle === 'rounded') {
      const radius = Math.min(scaledWidth, scaledHeight) * 0.15; 
      ctx.save();
      ctx.shadowColor = 'rgba(6, 182, 212, 0.6)'; 
      ctx.shadowBlur = 50;
      roundRectPath(ctx, x, y, scaledWidth, scaledHeight, radius);
      ctx.fillStyle = '#000';
      ctx.fill();
      ctx.restore();
      ctx.save();
      roundRectPath(ctx, x, y, scaledWidth, scaledHeight, radius);
      ctx.clip();
      ctx.drawImage(imageObj, x, y, scaledWidth, scaledHeight);
      ctx.restore();
      ctx.save();
      roundRectPath(ctx, x, y, scaledWidth, scaledHeight, radius);
      ctx.lineWidth = 4;
      ctx.strokeStyle = 'rgba(6, 182, 212, 0.5)';
      ctx.stroke();
      ctx.restore();
    } else if (edgeStyle === 'circle') {
      const radius = Math.min(scaledWidth, scaledHeight) / 2;
      ctx.save();
      ctx.shadowColor = 'rgba(249, 115, 22, 0.6)'; 
      ctx.shadowBlur = 50;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fillStyle = '#000';
      ctx.fill();
      ctx.restore();
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(imageObj, cx - radius, cy - radius, radius * 2, radius * 2);
      ctx.restore();
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.lineWidth = 5;
      const gradient = ctx.createLinearGradient(cx - radius, cy - radius, cx + radius, cy + radius);
      gradient.addColorStop(0, '#06b6d4'); 
      gradient.addColorStop(1, '#f97316'); 
      ctx.strokeStyle = gradient;
      ctx.stroke();
      ctx.restore();
    } else {
      ctx.drawImage(imageObj, x, y, scaledWidth, scaledHeight);
    }
  }, [imageObj, bgColor, edgeStyle, size]);

  const handleDownload = () => {
    if (!canvasRef.current) return;
    const url = canvasRef.current.toDataURL('image/jpeg', 1.0);
    const a = document.createElement('a');
    a.href = url;
    a.download = `RWAD_${size.id}_${size.width}x${size.height}.jpg`;
    a.click();
  };

  const Icon = size.icon;

  return (
    <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-4 border border-slate-700 flex flex-col items-center hover:border-cyan-500 transition-all hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(6,182,212,0.15)] shadow-lg group">
      <div className="w-full flex items-center justify-between mb-4 border-b border-slate-700 pb-3">
        <div className="flex items-center gap-2 text-cyan-400 group-hover:text-orange-400 transition-colors">
          <Icon className="w-5 h-5" />
          <h3 className="font-bold text-white text-sm">{size.name}</h3>
        </div>
        <span className="text-xs font-mono bg-slate-900/80 text-slate-400 px-2 py-1 rounded-md border border-slate-700/50" dir="ltr">
          {size.width} × {size.height}
        </span>
      </div>
      <div className="w-full bg-slate-950 rounded-xl mb-4 overflow-hidden flex items-center justify-center border border-slate-700/50 p-2 relative group-hover:border-slate-600 transition-colors" style={{ height: '180px' }}>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(#475569 1px, transparent 1px)', backgroundSize: '10px 10px' }}></div>
        <canvas ref={canvasRef} className="max-w-full max-h-full rounded-lg shadow-2xl relative z-10" style={{ objectFit: 'contain' }} />
      </div>
      <p className="text-xs text-slate-400 mb-4 text-center h-8 flex items-center">{size.desc}</p>
      <button onClick={handleDownload} className="w-full py-2.5 bg-gradient-to-r from-slate-700 to-slate-800 hover:from-cyan-600 hover:to-blue-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg border border-slate-600 hover:border-cyan-500">
        <Download className="w-4 h-4" /> تحميل بجودة HD
      </button>
    </div>
  );
};

const CompanyCircularLogo = ({ size = "lg" }) => {
  const dimensions = size === "lg" ? "w-28 h-28" : "w-16 h-16";
  const fontSize = size === "lg" ? "text-6xl" : "text-3xl";
  return (
    <div className={`relative flex items-center justify-center ${dimensions} rounded-full bg-[#0a1532] border-4 border-cyan-500 shadow-[0_0_30px_rgba(6,182,212,0.6)] overflow-hidden`}>
      <div className="absolute inset-0 rounded-full border-t-4 border-l-4 border-orange-500 opacity-90 rotate-45"></div>
      <div className="absolute inset-0 rounded-full border-b-4 border-r-4 border-blue-400 opacity-50 -rotate-12"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(6,182,212,0.4)_0%,transparent_70%)]"></div>
      <span className={`${fontSize} font-black italic text-transparent bg-clip-text bg-gradient-to-br from-orange-400 via-orange-500 to-yellow-500 drop-shadow-[0_0_10px_rgba(249,115,22,0.8)] relative z-10`} style={{ WebkitTextStroke: '1px rgba(255,255,255,0.2)' }}>R</span>
    </div>
  );
};

const AdsStudio = () => {
  const [imageObj, setImageObj] = useState(null);
  const [fileName, setFileName] = useState("");
  const [bgColor, setBgColor] = useState("#0a1532"); 
  const [edgeStyle, setEdgeStyle] = useState("vignette"); 

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => setImageObj(img);
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 md:p-6 font-sans text-right relative overflow-hidden" dir="rtl">
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-cyan-600/20 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-orange-600/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* الهيدر العلوي */}
        <header className="mb-12 text-center space-y-6 pt-6">
          <div className="flex flex-col items-center justify-center gap-4">
            <CompanyCircularLogo size="lg" />
            <h1 className="text-3xl md:text-5xl font-black text-white flex items-center justify-center gap-2">
              <span className="text-white">RWAD</span>
              <span className="text-cyan-400">Teck</span>
            </h1>
          </div>
          
          <div className="inline-flex items-center justify-center px-5 py-2 bg-cyan-500/10 text-cyan-400 rounded-full mb-2 border border-cyan-500/20 shadow-[0_0_15px_rgba(6,182,212,0.15)] backdrop-blur-md text-sm font-bold gap-2">
            <Sparkles className="w-4 h-4 text-orange-400" />
            أداة مجانية لتنسيق الشعارات من RWAD Technology
          </div>
          
          <p className="text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed">
            حل مشكلة تشوه الشعارات للأبد! ارفع شعارك، وسنقوم بضبطه وتوليد كافة المقاسات الرسمية لجميع منصات التواصل الاجتماعي بضغطة زر واحدة.
          </p>
        </header>

        {/* قسم رفع الصورة والإعدادات */}
        <div className="bg-slate-900/60 backdrop-blur-md p-6 md:p-8 rounded-3xl border border-slate-800 shadow-2xl mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
            
            <div className="col-span-1 relative h-full min-h-[220px]">
              <input 
                type="file" 
                accept="image/*" 
                onChange={handleImageUpload} 
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <div className={`h-full border-2 border-dashed rounded-2xl p-6 text-center flex flex-col items-center justify-center transition-all duration-300 ${imageObj ? 'border-cyan-500 bg-cyan-500/5 shadow-[0_0_20px_rgba(6,182,212,0.1)]' : 'border-slate-600 hover:border-cyan-500/50 bg-slate-800/50'}`}>
                {imageObj ? (
                  <>
                    <CheckCircle2 className="w-14 h-14 text-cyan-400 mb-3 drop-shadow-[0_0_8px_rgba(6,182,212,0.5)]" />
                    <h3 className="text-white font-bold text-lg mb-1">تم رفع الشعار بنجاح</h3>
                    <p className="text-slate-400 text-sm truncate max-w-[200px] bg-slate-950 px-3 py-1 rounded-full mt-2" dir="ltr">{fileName}</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-14 h-14 text-slate-400 mb-4 drop-shadow-md" />
                    <h3 className="text-white font-bold text-lg mb-2">ارفع صورة الشعار هنا</h3>
                    <p className="text-slate-400 text-sm">اضغط أو اسحب الصورة (JPG / PNG)</p>
                  </>
                )}
              </div>
            </div>

            <div className="col-span-1 lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-950/50 p-6 rounded-2xl border border-slate-800/80">
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-white mb-4">
                  <Shapes className="w-4 h-4 text-orange-400" />
                  أسلوب الدمج والحواف
                </label>
                <div className="space-y-3">
                  <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${edgeStyle === 'vignette' ? 'bg-cyan-900/30 border-cyan-500 text-cyan-300 shadow-[0_0_10px_rgba(6,182,212,0.2)]' : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-800'}`}>
                    <input type="radio" name="edgeStyle" value="vignette" checked={edgeStyle === 'vignette'} onChange={(e) => setEdgeStyle(e.target.value)} className="hidden" />
                    <div className="w-4 h-4 rounded-full border-2 border-current flex items-center justify-center">
                      {edgeStyle === 'vignette' && <div className="w-2 h-2 rounded-full bg-cyan-400"></div>}
                    </div>
                    <span className="font-bold text-sm">تلاشي ذكي ✨</span>
                  </label>

                  <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${edgeStyle === 'rounded' ? 'bg-cyan-900/30 border-cyan-500 text-cyan-300 shadow-[0_0_10px_rgba(6,182,212,0.2)]' : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-800'}`}>
                    <input type="radio" name="edgeStyle" value="rounded" checked={edgeStyle === 'rounded'} onChange={(e) => setEdgeStyle(e.target.value)} className="hidden" />
                    <div className="w-4 h-4 rounded-full border-2 border-current flex items-center justify-center">
                      {edgeStyle === 'rounded' && <div className="w-2 h-2 rounded-full bg-cyan-400"></div>}
                    </div>
                    <span className="font-bold text-sm">زوايا ناعمة + إضاءة</span>
                  </label>

                  <div className="grid grid-cols-2 gap-2">
                    <label className={`flex items-center justify-center gap-2 p-2 rounded-xl border cursor-pointer transition-all ${edgeStyle === 'circle' ? 'bg-cyan-900/30 border-cyan-500 text-cyan-300 shadow-[0_0_10px_rgba(6,182,212,0.2)]' : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-800'}`}>
                      <input type="radio" name="edgeStyle" value="circle" checked={edgeStyle === 'circle'} onChange={(e) => setEdgeStyle(e.target.value)} className="hidden" />
                      <span className="font-bold text-xs">دائري ⭕</span>
                    </label>
                    <label className={`flex items-center justify-center gap-2 p-2 rounded-xl border cursor-pointer transition-all ${edgeStyle === 'normal' ? 'bg-cyan-900/30 border-cyan-500 text-cyan-300 shadow-[0_0_10px_rgba(6,182,212,0.2)]' : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-800'}`}>
                      <input type="radio" name="edgeStyle" value="normal" checked={edgeStyle === 'normal'} onChange={(e) => setEdgeStyle(e.target.value)} className="hidden" />
                      <span className="font-bold text-xs">مربع ⬛</span>
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-white mb-4">
                  <Palette className="w-4 h-4 text-orange-400" />
                  اللون الخلفي المكمل
                </label>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-4 bg-slate-800/50 p-3 rounded-xl border border-slate-700">
                    <input 
                      type="color" 
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="w-12 h-12 rounded-lg cursor-pointer bg-transparent border-0 outline-none p-0 shadow-inner"
                    />
                    <div className="flex-1">
                      <p className="text-white font-bold text-sm mb-1">كود اللون</p>
                      <code className="text-cyan-400 font-mono text-sm">{bgColor}</code>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {imageObj ? (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 mb-20">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4 mb-8">
              <h2 className="text-2xl font-black text-white flex items-center gap-3">
                <div className="bg-cyan-500/20 p-2 rounded-lg"><Monitor className="text-cyan-400 w-6 h-6" /></div>
                المقاسات الجاهزة للتحميل
              </h2>
              <span className="bg-slate-800 text-slate-300 px-4 py-1.5 rounded-full text-sm font-bold border border-slate-700">6 مقاسات</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
              {SOCIAL_SIZES.map((size) => (
                <SizeCard key={size.id} size={size} imageObj={imageObj} bgColor={bgColor} edgeStyle={edgeStyle} />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-24 mb-20 bg-slate-900/30 rounded-3xl border border-slate-800/50 border-dashed">
            <div className="w-24 h-24 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6 opacity-40 shadow-inner">
              <ImageIcon className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-500">بانتظار رفع الشعار لبدء المعالجة... ✨</h3>
          </div>
        )}

        <div className="bg-gradient-to-b from-slate-900/80 to-slate-950 rounded-3xl border border-slate-800 p-8 shadow-2xl mb-16 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-600/5 rounded-full blur-[80px]"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative z-10">
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-orange-400 mb-4 border-b border-orange-400/20 pb-2">
                <Info className="w-6 h-6" />
                <h3 className="text-xl font-bold text-white">عن الأداة</h3>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">تم تطوير هذه الأداة المجانية كهدية من فريق <span className="text-cyan-400 font-bold">RWAD Technology</span> لمساعدة أصحاب المشاريع والمصممين في تجهيز شعاراتهم بجميع المقاسات الرسمية لمنصات التواصل الاجتماعي، مع ضمان الدقة والاحترافية العالية بضغطة زر وبدون الحاجة لبرامج تصميم معقدة.</p>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-cyan-400 mb-4 border-b border-cyan-400/20 pb-2">
                <Briefcase className="w-6 h-6" />
                <h3 className="text-xl font-bold text-white">من نحن؟</h3>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">نحن <strong className="text-white">RWAD Technology</strong>، شركة رائدة في تقديم الحلول البرمجية والتقنية المتكاملة. نهدف إلى مساعدة الشركات والمؤسسات على التحول الرقمي والنمو السريع من خلال أحدث التقنيات وأفضل الممارسات البرمجية العالمية.</p>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-emerald-400 mb-4 border-b border-emerald-400/20 pb-2">
                <Code2 className="w-6 h-6" />
                <h3 className="text-xl font-bold text-white">أبرز خدماتنا</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2.5 text-sm text-slate-300"><Store className="w-4 h-4 text-cyan-500 mt-0.5" /> <span>برمجة وتصميم المتاجر الإلكترونية (سلة، زد، برمجة خاصة).</span></li>
                <li className="flex items-start gap-2.5 text-sm text-slate-300"><Smartphone className="w-4 h-4 text-cyan-500 mt-0.5" /> <span>تطوير تطبيقات الهواتف الذكية (Android & iOS).</span></li>
                <li className="flex items-start gap-2.5 text-sm text-slate-300"><LayoutDashboard className="w-4 h-4 text-cyan-500 mt-0.5" /> <span>بناء الأنظمة الإدارية والمحاسبية وربط الـ API.</span></li>
                <li className="flex items-start gap-2.5 text-sm text-slate-300"><Sparkles className="w-4 h-4 text-cyan-500 mt-0.5" /> <span>حلول الذكاء الاصطناعي والاستشارات التقنية.</span></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="text-center sm:text-right">
              <h4 className="text-white font-bold mb-1 text-lg">هل لديك رؤية تقنية تود تنفيذها؟</h4>
              <p className="text-slate-400 text-sm">تواصل مع خبراء RWAD Technology الآن وحوّل فكرتك إلى واقع رقمي.</p>
            </div>
            <a href="https://wa.me/967779631649" target="_blank" rel="noreferrer" className="px-8 py-3.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white rounded-full font-bold flex items-center gap-2 transition-all shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:-translate-y-1">
              <WhatsAppIcon className="w-6 h-6" />
              <span>تواصل معنا عبر واتساب</span>
            </a>
          </div>
        </div>

        <footer className="mt-12 pt-10 pb-16 border-t border-slate-800/80 relative text-center">
          <div className="flex flex-col items-center justify-center gap-8">
            <div className="flex items-center gap-4">
              <a href="https://wa.me/967779631649" target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-emerald-500 hover:text-white transition-all shadow-lg hover:-translate-y-1" title="واتساب"><WhatsAppIcon className="w-6 h-6" /></a>
              <a href="https://www.facebook.com/profile.php?id=61588380693397" target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-blue-600 hover:text-white transition-all shadow-lg hover:-translate-y-1" title="فيسبوك"><Facebook className="w-6 h-6" /></a>
              <a href="mailto:rwd.technology@gmail.com" className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-orange-500 hover:text-white transition-all shadow-lg hover:-translate-y-1" title="إيميل"><Mail className="w-6 h-6" /></a>
              <a href="https://rwad-technology.com" target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-cyan-500 hover:text-white transition-all shadow-lg hover:-translate-y-1" title="موقعنا"><Globe className="w-6 h-6" /></a>
            </div>
            <p className="text-slate-300 flex items-center justify-center gap-2 text-sm md:text-base font-medium mt-2">تم التصميم والتطوير بكل <Heart className="w-4 h-4 text-red-500 fill-red-500 animate-pulse" /> بواسطة <span className="text-cyan-400 font-bold tracking-wide">RWAD Technology</span></p>
            <p className="text-slate-600 text-sm font-mono mt-4" dir="ltr">&copy; {new Date().getFullYear()} RWAD Technology. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AdsStudio;